import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, inject } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { DateUtility } from '../../date.utility';
import { DateFormatType } from '../../enums/date-format-type.enum';
import { ContractCP } from '../../models/contract-model';
import { RowLabelValue } from '../../models/row-label-value-model';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { FormatCurrencyPipe } from '../../pipes/format-currency.pipe';
import { AppFacadeService } from '../../services/app-facade.service';
import { ContractService } from '../../services/contract.service';
import { TranslateService } from '@ngx-translate/core';
import { DepotTimezoneFormatterService } from '../../services/depot-timezone-formatter.service';
import { OrganizationType } from '../../models/organization-type';
import { OrganizationDepot } from '../../models/organization-depot.model';
import { DepotTimezoneDateFormat } from '../../enums/depot-timezone-date-format.enum';
import { Subscription } from 'rxjs';

@Component({
    standalone: false,
    selector: 'por-contract-full-width-row',
    templateUrl: './contract-full-width-row.component.html',
    styleUrls: ['./contract-full-width-row.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractFullWidthRowComponent implements ICellRendererAngularComp, OnDestroy {
    private readonly date = inject(DatePipe);
    readonly featureService = inject(FeatureToggleService);
    private readonly formatCurrency = inject(FormatCurrencyPipe);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly translateService = inject(TranslateService);
    private readonly contractService = inject(ContractService);
    private readonly depotTimezoneFormatter = inject(DepotTimezoneFormatterService);

    public params!: ICellRendererParams;
    public contract!: ContractCP;

    public contractKV: RowLabelValue[] = [];
    public dateFormat = '';

    // Store config for timezone formatting
    private organizationType: OrganizationType | undefined;
    private depots: OrganizationDepot[] | undefined;
    private locale = 'en-US'; // Default to US locale

    // Subscription management
    private readonly subscription: Subscription = new Subscription();

    constructor() {
        this.dateFormat = DateUtility.getDateDisplayFormat(DateFormatType.StandardDate, 'en-US');

        // Subscribe to config to get organizationType, depots, and locale
        this.subscription.add(
            this.appFacadeService.getConfig().subscribe(config => {
                this.organizationType = config?.organizationType as OrganizationType | undefined;
                this.depots = config?.depots;
                // Get locale from organization config (e.g., 'en-GB' for Syrinx, 'en-US' for Elite)
                this.locale = config?.Locale || 'en-US';
            })
        );
    }

    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.contract = { ...params.data };
        this.contractKV = [
            {
                label: 'contract',
                value: this.contract?.contractName
            },
            {
                label: 'status',
                value: this.translateService.instant(this.contract?.status)
            },
            {
                label: 'startDate',
                value: this.formatDateWithTimezone(this.contract?.openDate, this.contract?.depotId)
            },
            {
                label: 'closeDate',
                value: this.formatDateWithTimezone(this.contract?.closeDate, this.contract?.depotId)
            },
            {
                label: 'purchaseOrder',
                value: this.contract?.purchaseOrder
            },
            {
                label: 'location',
                value: this.contract?.info
            }
        ];

        if (this.featureService.isAvailable('amoutDueFieldVisible')) {
            this.contractKV.splice(1, 0, {
                label: 'amountDue',
                value: this.contractService.getAmountDueText(this.contract.AmountDue, this.contract.paymentPending)
            });
        }

        if (this.featureService.isAvailable('amountFieldVisible')) {
            this.contractKV.splice(1, 0, {
                label: 'amount',
                value: this.formatCurrency.transform(String(this.contract.GrandTotal))
            });
        }

        if (this.featureService.isAvailable('contractGridJobColumnAvailable')) {
            this.contractKV.push({
                label: 'jobNumber',
                value: this.contract?.jobNumber
            });
        }
    }

    private formatDateWithTimezone(date: string | number | Date | null | undefined, depotId: string | undefined): string | null {
        if (!date) {
            return null;
        }

        // Get date only (MM/DD/YYYY for US, DD/MM/YYYY for UK)
        const dateOnlyStr = this.depotTimezoneFormatter.format(date, DepotTimezoneDateFormat.DateOnly, this.organizationType, this.depots, depotId, this.locale);

        // Syrinx systems should not show timezone
        if (this.featureService.isSyrinx()) {
            return dateOnlyStr;
        }

        // Get timezone abbreviation from full format
        const fullStr = this.depotTimezoneFormatter.format(date, DepotTimezoneDateFormat.Full, this.organizationType, this.depots, depotId, this.locale);
        const timezoneMatch = fullStr.match(/\s([A-Z]{3,4})$/);
        const timezone = timezoneMatch ? timezoneMatch[1] : '';

        // Combine: "12/2/2025 CST" (US) or "2/12/2025 GMT" (UK)
        return timezone ? `${dateOnlyStr} ${timezone}` : dateOnlyStr;
    }

    refresh() {
        return true;
    }

    loadContractDetail() {
        this.params.context?.componentParent.loadContractDetailModal(this.contract?.contractId);
    }

    onCheckboxChange(isChecked: boolean): void {
        if (this.params.context && this.params.context.componentParent) {
            this.params.context.componentParent.onFullWidthRowSelectionChanged(isChecked, this.contract.contractId);
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
